import styled, { css } from 'styled-components'
import { colors, device, fonts } from '../../../styles/GlobalVariables'
import { Container as PageColumn } from '../../Page/AuthorCategoryTag.styles'
import { Container as PageColumnFl } from '../../Page/AuthorCategoryTagFl.styles'
import { LiveVodContainer } from '../../Page/LiveVod.styles'
import { StoryStatus } from './storiesState'

/** Figma: "M_Home Page with 13 Shorts" (375) and "D_Home Page with 13 Shorts" (1920) */
const desktop = device.regular
export const RING_WATCHED = '#DCE1E7'
/** on a bubble whose focus draws no ring (StoriesRail's focusQuietly) */
export const QUIET_FOCUS = 'data-quiet-focus'
/** the brand ring in equal thirds, from the top clockwise: the bubbles and the
 * player's episode buttons */
export const RING_THIRDS = `conic-gradient(from 0deg, ${colors.pink} 0deg 120deg, ${colors.yellow} 120deg 240deg, ${colors.green} 240deg 360deg)`

const ringMaskAt = (width: string, half: number) => {
  const mask = `radial-gradient(closest-side, transparent calc(100% - ${width} - ${half}px), #000 calc(100% - ${width} + ${half}px), #000 calc(100% - ${half}px), transparent calc(100% + ${half}px))`
  return css`
    -webkit-mask: ${mask};
    mask: ${mask};
  `
}

/**
 * Keeps a band of `width` at the edge of a square box: a ring. Each edge gets
 * a ramp of one screen pixel, its anti-aliasing, so the band lands on the
 * design's radii. Rounding the box as well would soften the outer edge twice
 * (0.2px in).
 */
export const ringMask = (width: string) => css`
  ${ringMaskAt(width, 0.5)}

  @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 2dppx) {
    ${ringMaskAt(width, 0.25)}
  }

  @media (-webkit-min-device-pixel-ratio: 3), (min-resolution: 3dppx) {
    ${ringMaskAt(width, 0.167)}
  }
`

/**
 * 30px (mobile) and 40px (desktop) to the blocks around it, as drawn. In the
 * page column the column's own gap (30px, 28px from 1280) already stands
 * between blocks, so next to a neighbour the padding is what is left of the
 * design's space. The block sits in the column itself, or in a wrapper on the
 * home page; the FaceLift column has no gap.
 */
export const RailSection = styled.section`
  padding: 30px 0;

  /* mobile: the row runs to both screen edges, past the column's padding */
  ${PageColumn} > &,
  ${PageColumn} > * > & {
    margin-inline: -20px;
  }

  ${PageColumnFl} > &,
  ${PageColumnFl} > * > & {
    margin-inline: -16px;
  }

  ${PageColumn} > * + &,
  ${PageColumn} > * + * > & {
    padding-top: 0;
  }

  ${PageColumn} > &:not(:last-child),
  ${PageColumn} > *:not(:last-child) > & {
    padding-bottom: 0;
  }

  ${desktop} {
    padding: 40px 0;

    ${PageColumn} > &,
    ${PageColumn} > * > &,
    ${PageColumnFl} > &,
    ${PageColumnFl} > * > & {
      margin-inline: 0;
    }

    ${PageColumn} > * + &,
    ${PageColumn} > * + * > & {
      padding-top: 10px;
    }

    ${PageColumn} > &:not(:last-child),
    ${PageColumn} > *:not(:last-child) > & {
      padding-bottom: 10px;
    }
  }

  /* the live/vod column: its blocks keep 20px at the sides below 1280 and
     28px under them from 1280 */
  ${LiveVodContainer} > & {
    ${desktop} {
      margin-inline: 20px;
    }

    ${device.medium} {
      margin-inline: auto;
    }
  }

  ${device.medium} {
    ${LiveVodContainer} > * + & {
      padding-top: 12px;
    }

    ${PageColumn} > * + &,
    ${PageColumn} > * + * > & {
      padding-top: 12px;
    }

    ${PageColumn} > &:not(:last-child),
    ${PageColumn} > *:not(:last-child) > & {
      padding-bottom: 12px;
    }
  }
`

/** right to left; on mobile it runs off the left edge of the screen */
export const RailRow = styled.ul<{ $dragging: boolean }>`
  display: flex;
  gap: 12px;
  margin: 0;
  padding: 0 20px 0 0;
  list-style: none;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  /* a mouse drag scrolls the row: it selects nothing */
  user-select: none;
  -webkit-user-select: none;
  cursor: ${props => (props.$dragging ? 'grabbing' : 'auto')};

  &::-webkit-scrollbar {
    display: none;
  }

  /* the same space at the far end: padding at the end of a scrolling row is
     dropped by some browsers, so a spacer (12px gap + 8px = the 20px start) */
  &::after {
    content: '';
    flex: 0 0 8px;
  }

  /* the FaceLift column's padding is 16px */
  ${PageColumnFl} & {
    padding-right: 16px;

    &::after {
      flex-basis: 4px;
    }
  }

  /* the row scrolls; the bubbles keep their size */
  & > li {
    flex-shrink: 0;
  }

  ${desktop} {
    padding: 0;

    &::after {
      display: none;
    }

    ${PageColumnFl} & {
      padding-right: 0;
    }
  }
`

export const Bubble = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 90px;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  font-family: ${fonts.narkis};
  color: #000;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  /* the children take no taps: iOS drops a click whose target changes under
     the finger, and the press shade (picture::after) is such a change */
  & > * {
    pointer-events: none;
  }

  /* inside the bubble: the row clips at its edges, as drawn */
  &:focus-visible {
    outline: 2px solid ${colors.darkBlue};
    outline-offset: -2px;
    border-radius: 4px;
  }

  /* none, not even the browser's own, on the focus a closing viewer gives
     back after a tap (focusQuietly) */
  &[${QUIET_FOCUS}] {
    outline: none;
  }

  /* D_Story states: hover zooms the picture to 108%, a press shades it 25% */
  @media (hover: hover) {
    &:hover picture img {
      transform: scale(1.08);
    }
  }

  &:active picture::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.25);
  }

  ${desktop} {
    width: 100px;
  }
`

/**
 * The band only: between it and the picture the page shows through. Mobile
 * draws the watched band 4.091px and the others 4px; the picture stays 7px in.
 */
export const Ring = styled.span<{ $status: StoryStatus }>`
  position: relative;
  display: block;
  box-sizing: border-box;
  width: 90px;
  height: 90px;
  padding: ${props => (props.$status === 'watched' ? '4.091px' : '4px')};
  border-radius: 50%;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${props =>
      props.$status === 'watched' ? RING_WATCHED : RING_THIRDS};
    ${props => ringMask(props.$status === 'watched' ? '4.091px' : '4px')}
  }

  ${desktop} {
    width: 100px;
    height: 100px;
    padding: 4px;

    &::before {
      ${ringMask('4px')}
    }
  }
`

/** the transparent gap between the ring and the picture */
export const RingGap = styled.span<{ $status: StoryStatus }>`
  display: block;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding: ${props => (props.$status === 'watched' ? '2.909px' : '3px')};
  border-radius: 50%;

  picture {
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    overflow: hidden;
  }

  ${desktop} {
    padding: 3.778px;
  }
`

export const Cover = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  background-color: #fff;
  transition: transform 0.2s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

/**
 * Figma: 40x19 (mobile) and 50x22 (desktop) boxes with the text centred. A box
 * hugging its text came out 0.4px wider and a fraction taller, and the glyphs
 * sat 1px high on desktop: measured on the drawn glyphs (6px above, 7 below
 * in the design), the padding-top moves them there.
 */
export const NewBadge = styled.span`
  position: absolute;
  top: 73px;
  left: 50%;
  transform: translateX(-50%);
  box-sizing: border-box;
  width: 40px;
  height: 19px;
  padding: 2px 0;
  border-radius: 4px;
  background-color: ${colors.darkBlue};
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.1;
  text-align: center;
  white-space: nowrap;

  ${desktop} {
    top: 80px;
    width: 50px;
    height: 22px;
    padding: 3px 0 1px;
    font-size: 16px;
  }
`

export const Label = styled.span`
  display: -webkit-box;
  width: 90px;
  overflow: hidden;
  font-size: 14px;
  line-height: 14px;
  letter-spacing: 0.28px;
  text-align: center;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  /* a word wider than the label breaks, as in the design */
  overflow-wrap: break-word;

  b {
    font-weight: 700;
  }

  span {
    font-weight: 400;
  }
`
